import { AddOnOption } from "../types";
import { AddOnLearnMoreModal } from "./AddOnLearnMoreModal";
import { formatNullableCurrency } from "../../../utils/formatNullableCurrency";
import i18n from "../../../i18n";
import { Checkbox } from "@certn/cascadia-react";
import { Text } from "@certn/cascadia";
import { Link } from "@certn/cascadia";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { css } from "@emotion/css";
import compact from "lodash/compact";
import isEmpty from "lodash/isEmpty";

export type AddOnCheckboxesProps = {
  addOnOptions: AddOnOption[];
  checkArguments?: Record<string, unknown>;
  isDisabled?: boolean;
  currency?: string;
  handleAddOnOptionChange: (key: string, checked: boolean) => void;
  isQuickscreen: boolean;
};

export function AddOnCheckboxes({
  addOnOptions,
  checkArguments,
  isDisabled,
  currency,
  handleAddOnOptionChange,
  isQuickscreen,
}: AddOnCheckboxesProps) {
  const { t } = useTranslation();
  const [learnMoreOption, setLearnMoreOption] = useState<AddOnOption | null>(
    null
  );

  // Hide CV Review in Quickscreen flow
  const options = compact(
    addOnOptions.map((option) =>
      isQuickscreen && option.key === "include_cv_review" ? undefined : option
    )
  );

  if (isEmpty(options)) return null;

  return (
    <div
      className={css({
        padding: "var(--space-3) var(--space-4)",
        margin: "var(--space-0) var(--space-05) var(--space-0) var(--space-05)",
        backgroundColor: "var(--background-accent-neutral-weakest)",
        // Fix for duplicated rendering from <MediaQuery> using "display: none".
        // See https://github.com/Certn/certn-apps/pull/4543#discussion_r2612339166
        contain: "layout style",
        isolation: "isolate",
      })}
    >
      {options.map((option) => {
        const isChecked =
          (checkArguments?.[option.key] as boolean) ?? option.default;

        const formattedPrice = formatNullableCurrency(option.price, {
          currency: (option.currency || currency || "CAD") as string,
          locale: i18n.language,
          fallback: null,
        });

        return (
          <div key={option.key}>
            <div>
              <Checkbox
                testID={`add-on-${option.key}`}
                isSelected={isChecked}
                onChange={(checked: boolean) =>
                  handleAddOnOptionChange(option.key, checked)
                }
                isDisabled={isDisabled}
              >
                <Text style="input/label">
                  {option.label}
                  {formattedPrice && ` - ${formattedPrice}`}
                </Text>
              </Checkbox>
            </div>
            <div>
              <Text style="global/body-secondary" color="text-weak">
                {option.description}{" "}
              </Text>
              {option.learn_more && (
                <Link
                  textStyle="global/body-secondary"
                  textColor="text-weak"
                  onPress={() => setLearnMoreOption(option)}
                  testID={`add-on-${option.key}-learn-more`}
                >
                  {t("Learn more")}
                </Link>
              )}
            </div>
          </div>
        );
      })}
      {learnMoreOption && (
        <AddOnLearnMoreModal
          show={!!learnMoreOption}
          onClose={() => setLearnMoreOption(null)}
          data={learnMoreOption.learn_more}
        />
      )}
    </div>
  );
}
